import React from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    IconButton,
    Grid,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const formatNumberDefault = (num: number): string => {
    return new Intl.NumberFormat('es-ES').format(num);
};

export interface ShortFormDetailLayoutProps {
    /** Callback when back button is clicked */
    onBack: () => void;
    /** Avatar or icon (e.g. 56px Avatar) */
    avatar: React.ReactNode;
    /** Main title (e.g. @username or platform label) */
    title: React.ReactNode;
    /** Optional subtitle line(s) */
    subtitle?: React.ReactNode;
    /** Optional breadcrumbs above header */
    breadcrumbs?: React.ReactNode;
    /** Resumen section: summary label and cards */
    resumenTitle?: string;
    cards: Array<{ value: number; label: string }>;
    formatNumber?: (num: number) => string;
    /** Content section: section title and table (or any content) */
    contentTitle?: string;
    contentChildren?: React.ReactNode;
    /** Container top margin (e.g. for fixed header) */
    containerTopMargin?: number | object;
    /** Extra margin bottom for container */
    containerMarginBottom?: number;
}

const ShortFormDetailLayout: React.FC<ShortFormDetailLayoutProps> = ({
    onBack,
    avatar,
    title,
    subtitle,
    breadcrumbs,
    resumenTitle = 'Resumen',
    cards,
    formatNumber = formatNumberDefault,
    contentTitle,
    contentChildren,
    containerTopMargin = 4,
    containerMarginBottom = 4,
}) => {
    return (
        <Container
            maxWidth="xl"
            disableGutters
            sx={{
                mt: containerTopMargin,
                mb: containerMarginBottom,
                px: { xs: 2, sm: 3, md: 4 },
            }}
        >
            {breadcrumbs}

            {/* Header - same structure as creator detail */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={onBack} sx={{ mr: -1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    {avatar}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {typeof title === 'string' ? (
                                <Typography variant="h1" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 600, mb: 0.1 }}>
                                    {title}
                                </Typography>
                            ) : (
                                title
                            )}
                        </Box>
                        {subtitle && <Box sx={{ mt: 0.25 }}>{subtitle}</Box>}
                    </Box>
                </Box>
            </Box>

            {/* Resumen - same layout as creator detail */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                    {resumenTitle}
                </Typography>
                <Grid container spacing={2}>
                    {cards.map((card, index) => (
                        <Grid key={index} size={{ xs: 6, sm: 3 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h4" color="primary">
                                        {formatNumber(card.value)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {card.label}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Content section - optional */}
            {contentTitle != null && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                        {contentTitle}
                    </Typography>
                    {contentChildren}
                </Box>
            )}
        </Container>
    );
};

export default ShortFormDetailLayout;
